/**
 * Relevance ranking for every searchable list in the product.
 *
 * CONFIDENTIAL — ASI Internal.
 * Copyright 2026, Partnership Net; Agency for Sponsorship Intelligence, Lda;
 * Advanced Sponsorship Insights, GmbH. Confidential and proprietary.
 *
 * Framework-free (CLAUDE.md §1): no React, no DOM, no globals. The same file ranks
 * options in the browser today and query results in a Node service tomorrow.
 *
 * WHY THIS EXISTS. Every dropdown in the product used to be a raw `<MenuItem>` list in
 * source order with no search at all, so finding "Stadium Outside OOH" in ten channels
 * or "Hierarchical Bayesian MMM" in eight model types meant reading the whole list. The
 * fix is not "add a filter" — a naive `includes()` filter ranks a match at the end of a
 * long label the same as a match at the start, which is why typing "so" surfaced
 * "Website Brand-Owned" above "Social Paid".
 *
 * So matches are TIERED, best first:
 *   0  the whole label is the query          "TV" ⇢ "TV"
 *   1  the label starts with the query       "so" ⇢ "Social Paid"
 *   2  a word inside the label starts with it "paid" ⇢ "Social Paid"
 *   3  the query appears mid-word            "aid" ⇢ "Social Paid"
 *   +4 the same, matched on the hint rather than the label
 * A multi-word query must match EVERY word somewhere (AND, not OR) — "social paid"
 * should not return every social channel. Ties break on the shorter label, then
 * alphabetically, so the order is stable and never depends on input order.
 */

/** Characters that start a new word for tier-2 purposes. */
const SEPARATORS = new Set([
  ' ',
  '-',
  '–',
  '/',
  '\\',
  '(',
  ')',
  '[',
  ']',
  '.',
  ',',
  '&',
  '_',
  ':',
  "'",
]);

/** Case-folded, accent-stripped, whitespace-collapsed. "São Paulo" matches "sao". */
export function normalise(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/** Tier of a single token against a single already-normalised field. -1 = no match. */
export function fieldTier(hay: string, token: string): number {
  const i = hay.indexOf(token);
  if (i < 0) return -1;
  if (i === 0) return hay.length === token.length ? 0 : 1;
  return SEPARATORS.has(hay.charAt(i - 1)) ? 2 : 3;
}

/** Penalty added when a token only matched the hint, never the label. */
export const HINT_PENALTY = 4;

/**
 * Lower is better; `null` means the option does not match at all. An empty query
 * matches everything at score 0, which leaves the alphabetical order untouched.
 */
export function score(query: string, label: string, hint?: string): number | null {
  const q = normalise(query);
  if (!q) return 0;
  const l = normalise(label);
  const h = hint ? normalise(hint) : '';
  let worst = 0;
  let sum = 0;
  for (const token of q.split(' ')) {
    let tier = fieldTier(l, token);
    if (tier < 0 && h) {
      const ht = fieldTier(h, token);
      tier = ht < 0 ? -1 : ht + HINT_PENALTY;
    }
    if (tier < 0) return null;
    if (tier > worst) worst = tier;
    sum += tier;
  }
  // Worst tier dominates: one mid-word match drags the whole option down, which is what
  // stops a long label with an accidental substring outranking a clean prefix hit.
  return worst * 1000 + sum;
}

export interface Searchable {
  readonly label: string;
  readonly hint?: string;
}

/**
 * Filter and order a list. Pure: the input array is never mutated.
 *
 * WITH NO QUERY THE INCOMING ORDER IS PRESERVED, untouched. That is not a detail: the
 * caller has already sorted (alphabetically, or deliberately not — weekdays), and
 * re-sorting a zero-score list by length would silently override that. An earlier
 * version did exactly that and turned an alphabetical dropdown into a shortest-first one
 * the moment the search box was cleared.
 */
export function rank<T>(items: readonly T[], query: string, of: (item: T) => Searchable): T[] {
  if (!normalise(query)) return [...items];
  const scored: { item: T; s: number; label: string }[] = [];
  for (const item of items) {
    const f = of(item);
    const s = score(query, f.label, f.hint);
    if (s !== null) scored.push({ item, s, label: f.label });
  }
  // Ties break on the shorter label — a query that is most of a label is a better match
  // than the same query buried in a long one — then alphabetically, so the order is
  // stable and never depends on input order.
  scored.sort(
    (a, b) =>
      a.s - b.s ||
      a.label.length - b.label.length ||
      a.label.localeCompare(b.label, 'en', { sensitivity: 'base', numeric: true }),
  );
  return scored.map((x) => x.item);
}

/** Alphabetical, accent- and case-insensitive, digits ordered numerically. */
export function alphabetical<T>(items: readonly T[], of: (item: T) => string): T[] {
  return [...items].sort((a, b) =>
    of(a).localeCompare(of(b), 'en', { sensitivity: 'base', numeric: true }),
  );
}
